/**
 * Route Configuration
 * Centralized route constants for the application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  
  // Auth routes
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    VERIFY_EMAIL: '/verify-email',
  },
  
  // Student routes
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    EXAMS: '/student/exams',
    EXAM_DETAIL: '/student/exams/:examId',
    TAKE_EXAM: '/student/exams/:examId/take',
    RESULTS: '/student/results',
    RESULT_DETAIL: '/student/results/:attemptId',
    PROGRESS: '/student/progress',
    PROFILE: '/student/profile',
    SETTINGS: '/student/settings',
  },
  
  // Teacher routes
  TEACHER: {
    DASHBOARD: '/teacher/dashboard',
    EXAMS: '/teacher/exams',
    EXAM_CREATE: '/teacher/exams/create',
    EXAM_EDIT: '/teacher/exams/:examId/edit',
    EXAM_DETAIL: '/teacher/exams/:examId',
    EXAM_ANALYTICS: '/teacher/exams/:examId/analytics',
    QUESTIONS: '/teacher/questions',
    QUESTION_CREATE: '/teacher/questions/create',
    QUESTION_EDIT: '/teacher/questions/:questionId/edit',
    CLASSES: '/teacher/classes',
    CLASS_DETAIL: '/teacher/classes/:classId',
    GRADING: '/teacher/grading',
    ANALYTICS: '/teacher/analytics',
    PROFILE: '/teacher/profile',
    SETTINGS: '/teacher/settings',
  },
  
  // Admin routes
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: '/admin/users/:userId',
    EXAMS: '/admin/exams',
    ORGANIZATIONS: '/admin/organizations',
    ORGANIZATION_DETAIL: '/admin/organizations/:orgId',
    SCHOOLS: '/admin/schools',
    SCHOOL_DETAIL: '/admin/schools/:tenantId',
    SCHOOL_EDIT: '/admin/schools/:tenantId/edit',
    SCHOOL_ANALYTICS: '/admin/schools/:tenantId/analytics',
    SAAS_DATA: '/admin/saas-data',
    STUDENT_VIEW: '/admin/students/:studentId',
    TEACHER_VIEW: '/admin/teachers/:teacherId',
    SECURITY: '/admin/security',
    SETTINGS: '/admin/settings',
    REPORTS: '/admin/reports',
    AUDIT_LOG: '/admin/audit-log',
  },
  
  // Staff routes
  STAFF: {
    DASHBOARD: '/staff/dashboard',
    TEACHERS: '/staff/teachers',
    TEACHER_VIEW: '/staff/teachers/:teacherId',
    STUDENTS: '/staff/students',
    STUDENT_VIEW: '/staff/students/:studentId',
    CLASSES: '/staff/classes',
    CLASS_DETAIL: '/staff/classes/:classId',
    EXAMS: '/staff/exams',
    ANALYTICS: '/staff/analytics',
    SETTINGS: '/staff/settings',
  },
  
  // Parent routes
  PARENT: {
    DASHBOARD: '/parent/dashboard',
    CHILDREN: '/parent/children',
    CHILD_RESULTS: '/parent/children/:studentId/results',
    SETTINGS: '/parent/settings',
  },
  
  // Editor routes
  EDITOR: {
    DASHBOARD: '/editor',
    TABLE: '/editor/:tableName',
  },
  
  // SLP Editor routes
  SLP_EDITOR: {
    DASHBOARD: '/slp-editor',
    TABLE: '/slp-editor/:tableName',
  },
  
  // Student Learning Profile routes
  SLP: {
    DASHBOARD: '/slp',
    ACTIVITIES: '/slp/activities',
    PARTICIPATION: '/slp/participation',
    ATTENDANCE: '/slp/attendance',
  },
  
  // Calendar routes
  CALENDAR: {
    DASHBOARD: '/calendar',
    VIEW: '/calendar/view',
  },
  
  // Class Info routes
  CLASS_INFO: {
    DASHBOARD: '/class-info',
  },
  
  // ECA routes
  ECA: {
    DASHBOARD: '/eca',
    ACTIVITIES: '/eca/activities',
  },
  
  // Student routes (from MyClass)
  STUDENT_INFO: {
    DASHBOARD: '/student-info',
    PROFILE: '/student-info/:studentId',
  },
  
  // Error routes
  ERRORS: {
    NOT_FOUND: '/404',
    UNAUTHORIZED: '/401',
    FORBIDDEN: '/403',
    SERVER_ERROR: '/500',
  },
};

/**
 * Helper function to generate dynamic routes
 */
export const generateRoute = (route, params = {}) => {
  let path = route;
  Object.keys(params).forEach((key) => {
    path = path.replace(`:${key}`, params[key]);
  });
  return path;
};

/**
 * Route groups for navigation
 */
export const NAV_GROUPS = {
  STUDENT: [
    { path: ROUTES.STUDENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STUDENT.EXAMS, label: 'My Exams', icon: 'FileText' },
    { path: ROUTES.STUDENT.RESULTS, label: 'Results', icon: 'Award' },
    { path: ROUTES.STUDENT.PROGRESS, label: 'Progress', icon: 'TrendingUp' },
    { path: ROUTES.STUDENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  TEACHER: [
    { path: ROUTES.TEACHER.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.TEACHER.EXAMS, label: 'My Exams', icon: 'FileText' },
    { path: ROUTES.TEACHER.QUESTIONS, label: 'Question Bank', icon: 'HelpCircle' },
    { path: ROUTES.TEACHER.CLASSES, label: 'Classes', icon: 'Users' },
    { path: ROUTES.TEACHER.GRADING, label: 'Grading', icon: 'ClipboardCheck' },
    { path: ROUTES.TEACHER.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.TEACHER.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  ADMIN: [
    { path: ROUTES.ADMIN.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.ADMIN.SCHOOLS, label: 'Schools', icon: 'Building2' },
    { path: ROUTES.ADMIN.SAAS_DATA, label: 'SaaS Data', icon: 'Database' },
    { path: ROUTES.ADMIN.USERS, label: 'Users', icon: 'Users' },
    { path: ROUTES.ADMIN.EXAMS, label: 'All Exams', icon: 'FileText' },
    { path: ROUTES.ADMIN.SECURITY, label: 'Security', icon: 'Shield' },
    { path: ROUTES.ADMIN.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  STAFF: [
    { path: ROUTES.STAFF.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STAFF.TEACHERS, label: 'Teachers', icon: 'Users' },
    { path: ROUTES.STAFF.STUDENTS, label: 'Students', icon: 'GraduationCap' },
    { path: ROUTES.STAFF.CLASSES, label: 'Classes', icon: 'BookOpen' },
    { path: ROUTES.STAFF.EXAMS, label: 'Exams', icon: 'FileText' },
    { path: ROUTES.STAFF.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.STAFF.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  PARENT: [
    { path: ROUTES.PARENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.PARENT.CHILDREN, label: 'My Children', icon: 'Users' },
    { path: ROUTES.PARENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
};

export default ROUTES;
